import { API } from '../../api';
import {
  Avatar,
  Button,
  Checkbox,
  Col,
  Comment,
  Divider,
  Dropdown,
  Form,
  Input,
  List,
  Menu,
  Modal,
  Row,
  Select,
  notification,
} from 'antd';
import { DeleteOutlined, DownOutlined, EditOutlined, MessageOutlined } from '@ant-design/icons';
import {
  commentIsNotBlank,
  deleteComment,
  getChecklist,
  getComments,
  getDIMAUsers,
  getImplementationGuide,
  getReview,
  patchComment,
  postComment,
  sendErrorNotification,
} from '../../utils/util';
import { isEmpty } from 'lodash';
import MappingReviewWarnings from './mappingReviewWarnings';
import React, { useEffect, useState } from 'react';

const { TextArea } = Input;

const MappingReviewChecklist = props => {
  const { toggleModal, setLoading, setReviewStatus, mappingReviewId, checklistType, dataSource } =
    props;
  const [mappingReview, setMappingReview] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [users, setUsers] = useState([]);
  const [form] = Form.useForm();
  const [currComment, setCurrComment] = useState({ id: null });
  const [checklistComments, setChecklistComments] = useState([]);
  const [commentBoxValue, setCommentBoxValue] = useState('');
  const [showEditModal, setEditModal] = useState(false);
  const [editedComment, setEditedComment] = useState('');
  const [showDelModal, setDelModal] = useState(false);
  const [IG, setIG] = useState(null);

  const IG_TEXT = 'Link to IG used for mapping';

  const getMappingReview = async () => {
    const { mappingReview: mappingReviewData } = await getReview(mappingReviewId);
    const { status } = mappingReviewData;
    setReviewStatus(status);
    setMappingReview(mappingReviewData);
    setIG(getImplementationGuide(dataSource));
  };

  const getReviewChecklist = async () => {
    const chklist = await getChecklist(mappingReviewId, checklistType);
    setChecklist(chklist);
  };

  const getChecklistComments = async () => {
    const comments = await getComments(mappingReviewId, 'checkbox');
    setChecklistComments(comments);
  };

  const getUserList = async () => {
    let userList = await getDIMAUsers();
    userList = userList.filter(user => {
      if (user.id !== window.localStorage.getItem('user_id')) {
        return user;
      }

      return null;
    });
    setUsers(userList);
  };

  // show confirm message when reviews are requested or when reviewers are added to existing request
  const handleReviewerNotification = (isComplete, newReviewerList) => {
    if (checklistType !== 'requester' || newReviewerList.length === 0) return;

    let emailList = [];

    if (isComplete) {
      emailList = users.filter(({ id }) => newReviewerList.includes(id));
    } else if (mappingReview.request_date) {
      // if a review has already been requested, show users only that were added to request
      emailList = users.filter(
        ({ id }) => newReviewerList.includes(id) && !mappingReview.reviewers.includes(id),
      );
    }

    if (emailList.length === 0) return;

    emailList = emailList.map(({ name, email }) => `${name} (${email})`);

    const key = `open${Date.now()}`;
    const btn = (
      <Button type="primary" size="small" onClick={() => notification.close(key)}>
        Close
      </Button>
    );
    notification.open({
      message: 'Review Requested!',
      description: (
        <>
          Request for review has been sent to <strong>{emailList.join(', ')}</strong>. To view the
          status of your request, go to the summary page.
        </>
      ),
      btn,
      key,
      onClose: () => {},
    });
  };

  const handleSave = async (isComplete = false) => {
    const formValues = form.getFieldsValue();
    const body = {
      checklistItems: [],
      mappingReviewId,
      type: checklistType,
      isComplete,
    };
    if (checklistType === 'requester') body.reviewers = formValues.reviewers;

    delete formValues.reviewers;
    const checklistItems = checklist.map(
      ({ checklist_state_id: checklistStateId, input_type: inputType }) => {
        return {
          id: checklistStateId,
          type: inputType,
          value: formValues[checklistStateId],
        };
      },
    );

    body.checklistItems = checklistItems;

    if (process.env.REACT_APP_CICD) {
      body.cypressTest = true;
    }

    if (!isEmpty(body)) {
      await API.patch('/mapping/review/checklist', body).catch(() => {
        sendErrorNotification(
          'There was an error saving your updates to the mapping review checklist.',
        );
      });
      await getReviewChecklist();

      if (isComplete) {
        props.getMappingReview(props.fileId);
      }

      toggleModal(false);

      if (body.reviewers) handleReviewerNotification(isComplete, body.reviewers);
    } else {
      toggleModal(false);
    }
  };

  const onFinishFailed = errorInfo => {
    notification.error({
      message: 'ERROR:',
      description: errorInfo,
      duration: 6,
    });
  };

  const toggleDelModal = () => {
    setDelModal(!showDelModal);
    return null;
  };

  const toggleEditModal = () => {
    setEditModal(!showEditModal);
    return null;
  };

  const getInitialFormValues = () => {
    const initialValues = {};
    checklist.forEach(checklistItem => {
      const {
        checklist_state_id: checklistStateId,
        input_type: inputType,
        checked,
        response,
      } = checklistItem;
      if (inputType === 'checkbox') initialValues[checklistStateId] = checked;
      if (inputType === 'input') initialValues[checklistStateId] = response || '';

      if (checklistItem.description[0] === IG_TEXT) {
        initialValues[checklistStateId] = IG;
      }
    });
    if (checklistType === 'requester') initialValues.reviewers = mappingReview.reviewers;
    return initialValues;
  };

  const setCurrentCommentId = checklistStateId => {
    if (currComment.id === checklistStateId) {
      setCurrComment({ id: null });
      return null;
    }

    setCurrComment({ id: checklistStateId });
    return checklistStateId;
  };

  const editChecklistComment = async () => {
    // ensures comment is not blank
    if (commentIsNotBlank(editedComment)) {
      const success = await patchComment(
        mappingReviewId,
        'checkbox',
        editedComment,
        currComment.id,
      );

      if (success) {
        await getChecklistComments();
      }
    }

    setEditModal(false);
  };

  const handleDeleteChecklistComment = async () => {
    const success = await deleteComment(currComment.id);

    if (success) {
      getChecklistComments();
    }

    toggleDelModal();
  };

  const handleCommentBoxChange = ({ target }) => {
    const { value } = target;
    setCommentBoxValue(value);
  };

  const handleEditedCommentBoxChange = ({ target }) => {
    const { value } = target;
    setEditedComment(value);
  };

  const handleSubmitChecklistComment = async () => {
    // ensures comment is not blank
    if (commentIsNotBlank(commentBoxValue)) {
      const success = await postComment(
        mappingReviewId,
        'checkbox',
        commentBoxValue,
        currComment.id,
      );

      if (success) {
        getChecklistComments();
        setCommentBoxValue('');
        setCurrentCommentId('');
      }
    }
  };

  const generateFormItems = () => {
    let e2eInputNumber = 1;
    const formItems = checklist.map(checklistItem => {
      const {
        checklist_state_id: checklistStateId,
        input_type: inputType,
        description,
      } = checklistItem;
      const itemText = description.join(' ');

      if (inputType === 'checkbox') {
        const comments = checklistComments.filter(
          comment => comment.checkbox_id === checklistStateId,
        );
        return (
          <>
            <Row>
              <Col style={{ width: '90%' }}>
                <Form.Item
                  id={checklistStateId}
                  key={checklistStateId}
                  name={checklistStateId}
                  valuePropName="checked"
                  wrapperCol={{ offset: 0, span: 64 }}
                  rules={[
                    {
                      required: true,
                      transform: value => value || undefined,
                      type: 'boolean',
                      message: 'Required',
                    },
                  ]}
                >
                  <Checkbox data-cy="mapping-review-checkbox">{itemText}</Checkbox>
                </Form.Item>
              </Col>
              <Col style={{ width: '10%' }}>
                <Button
                  type="primary"
                  icon={<MessageOutlined />}
                  size="default"
                  data-cy="checklist-comment-btn"
                  onClick={e => [e.stopPropagation(), setCurrentCommentId(checklistStateId)]}
                />
              </Col>
            </Row>
            {currComment.id === checklistStateId && (
              <Row>
                <div style={{ width: '100%', justifyContent: 'center' }}>
                  <TextArea
                    onChange={handleCommentBoxChange}
                    value={commentBoxValue}
                    rows={2}
                    placeholder="Type Comment Here"
                    data-cy="checklist-comment-input"
                    maxLength={255}
                  />
                  <br />
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      data-cy="submit-checklist-comment"
                      onClick={handleSubmitChecklistComment}
                    >
                      Submit Comment
                    </Button>
                  </div>
                  <br />
                </div>
              </Row>
            )}
            {comments && comments.length > 0 && (
              <Row style={{ marginTop: '-25px', width: '90%' }}>
                <List
                  style={{ marginLeft: '25px', minWidth: '100%' }}
                  itemLayout="horizontal"
                  dataSource={comments}
                  renderItem={c => (
                    <List.Item style={{ textAlign: 'left' }} size="small">
                      <>
                        <div>
                          <Comment
                            avatar={
                              <Avatar size="small" style={{ backgroundColor: '#ca77f7' }}>
                                {c.name[0]}
                              </Avatar>
                            }
                            author={c.name}
                            content={c.comment}
                            datetime={new Date(c.updated_at).toLocaleString()}
                            style={{ width: '90%', whiteSpace: 'pre-wrap' }}
                          />
                        </div>

                        <Dropdown.Button
                          trigger={['click']}
                          size="small"
                          disabled={c.user_id !== window.localStorage.getItem('user_id')}
                          data-cy={`open-checklist-comment-${c.user_id}`}
                          placement="bottomRight"
                          overlayStyle={{
                            border: '1px solid #d9d9d9',
                            borderRadius: 2,
                          }}
                          icon={<DownOutlined id={`dropdownExtension${c.id}`} />}
                          overlay={
                            <Menu>
                              <Menu.Item
                                key="1"
                                data-cy="edit-comment"
                                id={`edit${c.id}`}
                                onClick={() => [
                                  setCurrComment(c),
                                  setEditedComment(c.comment),
                                  setEditModal(true),
                                ]}
                                icon={<EditOutlined />}
                                type="primary"
                              >
                                Edit
                              </Menu.Item>
                              <Menu.Item
                                key="2"
                                data-cy="delete-comment"
                                id={`delete${c.id}`}
                                onClick={() => [setCurrComment(c), setDelModal(true)]}
                                icon={<DeleteOutlined />}
                                type="primary"
                              >
                                Delete
                              </Menu.Item>
                            </Menu>
                          }
                        />
                      </>
                    </List.Item>
                  )}
                />
              </Row>
            )}
          </>
        );
      }

      if (inputType === 'input') {
        // for e2e tests
        const formInput = (
          <Form.Item
            label={itemText}
            name={checklistStateId}
            key={checklistStateId}
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input
              data-lpignore="true"
              disabled={itemText === IG_TEXT}
              data-cy={`mapping-review-input-${e2eInputNumber}`}
            />
          </Form.Item>
        );
        e2eInputNumber += 1;
        return formInput;
      }

      return null;
    });

    const reviewerSelect = (
      <Form.Item
        label="Reviewers"
        name="reviewers"
        key="reviewers"
        rules={[{ required: true, message: 'Required' }]}
      >
        <Select
          mode="multiple"
          placeholder="select reviewer"
          data-cy="mapping-review-reviewer-dropdown"
          style={{
            width: '100%',
          }}
        >
          {users.map(({ name, id, email }) => {
            const displayText = `${name} (${email})`;
            return (
              <Select.Option key={id} value={id}>
                {displayText}
              </Select.Option>
            );
          })}
        </Select>
      </Form.Item>
    );

    if (checklistType === 'requester') formItems.push(reviewerSelect);

    return formItems;
  };

  useEffect(() => {
    if (mappingReview) {
      form.setFieldsValue(getInitialFormValues());
    }
  }, [checklist, checklistComments, currComment, showDelModal, showEditModal, IG]);

  useEffect(() => {
    setLoading(true);

    if (props.fileId) {
      (async () => {
        await getChecklistComments();
        await getMappingReview();
        await getReviewChecklist();
        if (checklistType === 'requester') await getUserList();
        setLoading(false);
      })();
    }
  }, [checklistType]);

  if (checklist.length === 0) return null;

  const drawerHeader = checklistType.charAt(0).toUpperCase() + checklistType.slice(1);

  return (
    <>
      <Modal
        title="Delete Checklist Comment"
        maskClosable
        visible={showDelModal}
        footer={null}
        onCancel={toggleDelModal}
      >
        <p>Are you sure you want to delete the following comment:</p>
        <p>{currComment.comment}</p>
        <br />
        <Button
          type="danger"
          htmlType="submit"
          onClick={e => [e.stopPropagation(), handleDeleteChecklistComment()]}
          data-cy="delete-checklist-comment-btn"
        >
          Delete
        </Button>
      </Modal>
      <Modal
        title="Edit Checklist Comment"
        maskClosable
        visible={showEditModal}
        footer={null}
        onCancel={toggleEditModal}
      >
        <div style={{ width: '90%', justifyContent: 'center' }}>
          <TextArea
            onChange={handleEditedCommentBoxChange}
            value={editedComment}
            data-cy="edit-checklist-comment-input"
            rows={2}
          />
        </div>
        <br />
        <Button
          type="default"
          onClick={e => [e.stopPropagation(), editChecklistComment()]}
          data-cy="submit-checklist-edit"
        >
          Submit
        </Button>
      </Modal>
      <Divider orientation="center">
        {' '}
        <h2>{drawerHeader} Checklist</h2>
      </Divider>
      <Form
        form={form}
        name="basic"
        labelWrap
        labelAlign="left"
        labelCol={{ span: 16 }}
        wrapperCol={{ span: 32 }}
        size="medium"
        onFinish={() => handleSave(true)}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
        validateTrigger="onSubmit"
      >
        {generateFormItems()}
      </Form>
      <MappingReviewWarnings IG={IG} dataSource={dataSource} />
      <div style={{ display: 'flex', justifyContent: 'space-around' }}>
        <Button
          type="primary"
          disabled={mappingReview.status === 'complete'}
          onClick={() => handleSave()}
        >
          Save Progress
        </Button>
        <Button
          type="primary"
          disabled={
            (checklistType === 'requester' && mappingReview.status !== 'in progress') ||
            mappingReview.status === 'complete'
          }
          onClick={() => form.submit()}
        >
          {checklistType === 'requester' ? 'Request' : 'Complete'} Review
        </Button>
      </div>
    </>
  );
};

export default MappingReviewChecklist;
